/**
 * 奖池头与选号器的纯展示逻辑。定稿 `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §4，
 * 以及 2026-08-19 评审新增的「用选号器几何讲中奖规则」。
 */

/**
 * 把「已经滚了 N 期」拆成三段，好让中间那个数字单独加重。
 *
 * <p>§6.1 论证过：巴西彩票受众数的是**期数**不是金额 —— 金额他们没有参照系，
 * 期数有。所以这一行是整个玩法情绪的锚点，数字必须能被一眼抓到。
 *
 * <p>模板里没有 `%n%` 时返回 null（整行不渲染），与 `poolRoundsLine` 同口径。
 */
export function poolRoundsParts(
  roundsAccumulating: number | null | undefined,
  template: string,
): { before: string; count: string; after: string } | null {
  if (typeof roundsAccumulating !== 'number') return null;
  if (!Number.isFinite(roundsAccumulating) || roundsAccumulating <= 0) return null;
  const index = template.indexOf('%n%');
  if (index < 0) return null;
  return {
    before: template.slice(0, index),
    count: String(Math.floor(roundsAccumulating)),
    after: template.slice(index + 3),
  };
}

export type PayoutSlot =
  /** 全中：奖池，金额由平分决定，前端不自算 */
  | { matched: number; kind: 'jackpot' }
  /** 固定赔率档：注额 × 倍数 */
  | { matched: number; kind: 'fixed'; cents: number };

/**
 * 选号器每一格下面标什么。
 *
 * <p>规则的本质是**位置**：从右往左对中几位，就拿第几档。而位置正是选号器已经
 * 在展示的东西 —— 把奖金标在格子下面，规则就不用被讲述，它被看见。这也和开奖
 * reveal 的逐位点亮是同一套空间语言。
 *
 * <p>返回值顺序与格子一致（最左在前）。最左那格永远是 jackpot：对中全部位数。
 *
 * <p><b>倍数必须来自 config，不许前端写死</b> —— 视觉规格的既有硬约束，赔率是
 * 配置项，写死会在调档时变成假信息。缺某一档倍数时该格返回 null 而不是编一个数。
 */
export function pickerPayoutSlots(
  digits: number,
  multipliers: Record<string, string> | undefined,
  stakeCents: number,
): (PayoutSlot | null)[] {
  if (!Number.isFinite(digits) || digits <= 0) return [];
  return Array.from({ length: digits }, (_, index) => {
    const matched = digits - index;
    if (matched === digits) return { matched, kind: 'jackpot' as const };
    const raw = multipliers?.[String(matched)];
    const factor = raw === undefined ? Number.NaN : Number(raw);
    if (!Number.isFinite(factor) || factor <= 0) return null;
    return { matched, kind: 'fixed' as const, cents: Math.round(stakeCents * factor) };
  });
}

/**
 * 在一组离散档位里走一步。
 *
 * <p>期数改成 `+ / −` 之后如果按 1 递增，选 20 期要点 19 下 —— teimosinha 是营收
 * 功能，那种摩擦会直接压低它的使用率。所以步进走的是**档位**（1→5→10→20），
 * 形态是步进器，代价不是。
 *
 * <p>当前值不在档位里时（配置变过、或旧的持久化值），就近吸附到不越界的一档，
 * 而不是重置成第一档 —— 重置会把用户已经选好的期数悄悄改小。
 */
export function stepInOptions(options: number[], current: number, direction: 1 | -1): number {
  const sorted = options.filter((n) => Number.isFinite(n)).slice().sort((a, b) => a - b);
  if (!sorted.length) return current;
  const index = sorted.indexOf(current);
  if (index >= 0) {
    const next = index + direction;
    return next < 0 || next >= sorted.length ? current : sorted[next];
  }
  if (direction === 1) return sorted.find((n) => n > current) ?? sorted[sorted.length - 1];
  return [...sorted].reverse().find((n) => n < current) ?? sorted[0];
}

/**
 * 期数步进器的形态。
 *
 * <p>2026-08-19 评审：`+` 一次只加 1。原本走的是档位（1→5→10→20），因为
 * teimosinha 是营收功能、选 20 期点 19 下会压使用率 —— 但代价是「选 2 期」这个
 * 完全合理的意图根本表达不出来，而它恰恰是新用户最可能想试的那一档。
 *
 * <p><b>`free` 由后端的 `maxRounds` 决定，不是前端的偏好开关。</b> 后端曾把这几档
 * 同时当成校验（`MILHAR_INVALID_ROUNDS`），所以只要它还没放开，前端就必须继续
 * 按档走：否则用户能选出 2 期，一路走到付款那一刻才被拒。前端可以先于后端部署，
 * 这个降级就是代价 —— 但降级是「少一个功能」，不是「买不了」。
 */
export type RoundsStepper = {
  /** true = 自由整数步进；false = 只在 `options` 的档位间跳。 */
  free: boolean;
  min: number;
  max: number;
  options: number[];
};

export function roundsStepper(
  teimosinhaOptions: number[] | undefined,
  maxRounds: number | undefined,
): RoundsStepper {
  const options = (teimosinhaOptions ?? [])
    .filter((n) => Number.isFinite(n) && n >= 1)
    .slice()
    .sort((a, b) => a - b);
  const declared = Number(maxRounds);
  const free = Number.isFinite(declared) && declared >= 1;
  const fallbackMax = options.length ? options[options.length - 1] : 1;
  return {
    free,
    min: 1,
    max: free ? declared : fallbackMax,
    options: options.length ? options : [1],
  };
}

/**
 * 走一步。
 *
 * <p>两种形态都**夹在 [min, max] 里**，而不是靠调用方 disable 按钮来兜底：按钮的
 * disabled 是给眼睛看的，越界防护得长在值上面，否则键盘和辅助技术能绕过去。
 */
export function stepRounds(stepper: RoundsStepper, current: number, direction: 1 | -1): number {
  if (!stepper.free) return stepInOptions(stepper.options, current, direction);
  const base = Number.isFinite(current) ? Math.round(current) : stepper.min;
  const next = base + direction;
  return Math.min(stepper.max, Math.max(stepper.min, next));
}
